/**
 * QuantumFlowSync.AI - Enhanced Roles Management Page
 * Role cards organized by tier with comparison tool
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Users, ChevronDown, ChevronRight, Plus,
  Check, X, RefreshCw, Eye, Edit, GitCompare
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TierGate } from '../../components/PermissionGate';

const EnhancedRoleManagementPage = () => {
  const { apiRequest } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTiers, setExpandedTiers] = useState([1, 2, 3, 4, 5, 6]);
  const [expandedRoles, setExpandedRoles] = useState([]);
  
  // Comparison state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  
  // Tier information
  const tierInfo = {
    1: { name: 'Executive Leadership', color: 'purple', description: 'C-level and executive roles' },
    2: { name: 'Senior Management', color: 'indigo', description: 'Department heads and senior managers' },
    3: { name: 'Middle Management', color: 'blue', description: 'Team leads and managers' },
    4: { name: 'Professional Staff', color: 'green', description: 'Specialists and senior employees' },
    5: { name: 'General Staff', color: 'yellow', description: 'Standard employees' },
    6: { name: 'Entry Level', color: 'gray', description: 'Junior staff and contractors' }
  };
  
  // Fetch roles
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiRequest('/api/admin/roles');
      
      if (response.success) {
        setRoles(response.data.roles || []);
      } else {
        setError(response.error || 'Failed to fetch roles');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };
  
  // Toggle tier expansion
  const toggleTier = (tier) => {
    if (expandedTiers.includes(tier)) {
      setExpandedTiers(expandedTiers.filter(t => t !== tier));
    } else {
      setExpandedTiers([...expandedTiers, tier]);
    }
  };
  
  // Toggle role expansion
  const toggleRole = (roleId) => {
    if (expandedRoles.includes(roleId)) {
      setExpandedRoles(expandedRoles.filter(id => id !== roleId));
    } else {
      setExpandedRoles([...expandedRoles, roleId]);
    }
  };
  
  // Toggle comparison selection
  const toggleCompare = (roleId) => {
    if (selectedForCompare.includes(roleId)) {
      setSelectedForCompare(selectedForCompare.filter(id => id !== roleId));
    } else if (selectedForCompare.length < 2) {
      setSelectedForCompare([...selectedForCompare, roleId]);
    }
  };
  
  // Group roles by tier
  const rolesByTier = roles.reduce((acc, role) => {
    if (!acc[role.tier]) {
      acc[role.tier] = [];
    }
    acc[role.tier].push(role);
    return acc;
  }, {});
  
  // Effect: Fetch roles on mount
  useEffect(() => {
    fetchRoles();
  }, []);
  
  // Get tier color classes
  const getTierColor = (tier) => {
    const colors = {
      purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    };
    return colors[tierInfo[tier]?.color] || colors.gray;
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Roles & Permissions
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage roles and their permissions across all tiers
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCompareMode(!compareMode)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              compareMode
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <GitCompare className="w-4 h-4" />
            Compare Roles
          </button>
          
          <button
            onClick={fetchRoles}
            disabled={loading}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          
          <TierGate tier={1}>
            <button
              onClick={() => navigate('/admin/roles/new')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Custom Role
            </button>
          </TierGate>
        </div>
      </div>
      
      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <span className="text-red-800 dark:text-red-200">{error}</span>
        </div>
      )}
      
      {/* Comparison Bar */}
      {compareMode && (
        <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-indigo-900 dark:text-indigo-200">
                Comparison Mode: Select 2 roles to compare
              </span>
              {selectedForCompare.length === 2 && (
                <span className="ml-3 text-sm text-indigo-700 dark:text-indigo-300">
                  Ready to compare!
                </span>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {selectedForCompare.length === 2 && (
                <button
                  onClick={() => {/* Show comparison modal */}}
                  className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
                >
                  View Comparison
                </button>
              )}
              <button
                onClick={() => {
                  setCompareMode(false);
                  setSelectedForCompare([]);
                }}
                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        /* Tiers */
        <div className="space-y-4">
          {[1, 2, 3, 4, 5, 6].map((tier) => {
            const tieredRoles = rolesByTier[tier] || [];
            const isExpanded = expandedTiers.includes(tier);
            
            return (
              <div
                key={tier}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                {/* Tier Header */}
                <button
                  onClick={() => toggleTier(tier)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-500" />
                    )}
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getTierColor(tier)}`}>
                      Tier {tier}
                    </span>
                    <span className="text-lg font-semibold text-gray-900 dark:text-white">
                      {tierInfo[tier]?.name}
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {tierInfo[tier]?.description}
                    </span>
                  </div>
                  
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {tieredRoles.length} role{tieredRoles.length !== 1 ? 's' : ''}
                  </span>
                </button>
                
                {/* Tier Content */}
                {isExpanded && (
                  <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                    {tieredRoles.length === 0 ? (
                      <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                        No roles in this tier
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {tieredRoles.map((role) => {
                          const isRoleExpanded = expandedRoles.includes(role.id);
                          const isSelected = selectedForCompare.includes(role.id);
                          
                          return (
                            <div
                              key={role.id}
                              className={`border rounded-lg overflow-hidden transition-all ${
                                isSelected
                                  ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                                  : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900'
                              }`}
                            >
                              {/* Role Card Header */}
                              <div className="p-4">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <Shield className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                        {role.display_name}
                                      </h3>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                      {role.description}
                                    </p>
                                    
                                    <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
                                      <div className="flex items-center">
                                        <Users className="w-4 h-4 mr-1" />
                                        {role.employee_count || 0} employees
                                      </div>
                                      <div className="flex items-center">
                                        <Shield className="w-4 h-4 mr-1" />
                                        {role.permission_count || 0} permissions
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className="flex items-center gap-2">
                                    {compareMode && (
                                      <button
                                        onClick={() => toggleCompare(role.id)}
                                        disabled={!isSelected && selectedForCompare.length >= 2}
                                        className={`p-2 rounded-lg ${
                                          isSelected
                                            ? 'bg-indigo-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                      >
                                        {isSelected ? <Check className="w-4 h-4" /> : <GitCompare className="w-4 h-4" />}
                                      </button>
                                    )}
                                    
                                    <button
                                      onClick={() => toggleRole(role.id)}
                                      className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600"
                                    >
                                      {isRoleExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                    </button>
                                    
                                    <button
                                      onClick={() => navigate(`/admin/roles/${role.id}`)}
                                      className="p-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Role Card Expanded Content */}
                              {isRoleExpanded && (
                                <div className="border-t border-gray-200 dark:border-gray-700 p-4 space-y-4">
                                  {/* Permissions */}
                                  <div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                      Permissions
                                    </h4>
                                    <div className="space-y-1 max-h-48 overflow-y-auto">
                                      {role.permissions && role.permissions.length > 0 ? (
                                        role.permissions.map((perm, idx) => (
                                          <div
                                            key={idx}
                                            className="flex items-center text-xs text-gray-600 dark:text-gray-400"
                                          >
                                            <Check className="w-3 h-3 mr-2 text-green-600" />
                                            {perm}
                                          </div>
                                        ))
                                      ) : (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                          No permissions assigned
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Assigned Employees */}
                                  {role.employees && role.employees.length > 0 && (
                                    <div>
                                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        Assigned Employees
                                      </h4>
                                      <div className="space-y-1">
                                        {role.employees.slice(0, 5).map((emp) => (
                                          <div
                                            key={emp.id}
                                            className="text-xs text-gray-600 dark:text-gray-400"
                                          >
                                            {emp.name} ({emp.email})
                                          </div>
                                        ))}
                                        {role.employees.length > 5 && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400">
                                            +{role.employees.length - 5} more
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnhancedRoleManagementPage;
